import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Copy, Check, Gift, Users, TrendingUp, Loader2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useWallet } from "@/contexts/WalletContext";
import { useQuery } from "@tanstack/react-query";

export function ReferralSystem() {
  const { toast } = useToast();
  const { connected, publicKey } = useWallet();
  const [copied, setCopied] = useState(false);
  const [referralCode, setReferralCode] = useState<string | null>(null);

  // Generate or get referral code
  useEffect(() => {
    if (!publicKey) return;
    
    const generateCode = () => {
      // Create a short unique code from wallet
      const hash = publicKey.slice(0, 4) + publicKey.slice(-4);
      return `TMR${hash.toUpperCase()}`;
    };
    
    setReferralCode(generateCode());
  }, [publicKey]);

  // Get referral stats
  const { data: stats } = useQuery({
    queryKey: ["referral-stats", publicKey],
    queryFn: async () => {
      if (!publicKey) return null;
      
      const { data: referrals } = await supabase
        .from("referrals")
        .select("*")
        .eq("referrer_wallet", publicKey);
      
      const total = referrals?.length || 0;
      const completed = referrals?.filter(r => r.status === "completed").length || 0;
      const earnings = referrals?.reduce((sum, r) => sum + (r.reward_amount || 0), 0) || 0;
      
      return { total, completed, earnings };
    },
    enabled: connected && !!publicKey,
  });

  const referralUrl = referralCode 
    ? `${window.location.origin}?ref=${referralCode}` 
    : "";

  const copyLink = async () => {
    await navigator.clipboard.writeText(referralUrl);
    setCopied(true);
    toast({ title: "Referral Link Copied!", description: "Share to earn rewards" });
    setTimeout(() => setCopied(false), 2000);
  };

  if (!connected) {
    return (
      <div className="p-6 rounded-xl bg-card border border-border text-center">
        <Gift className="w-10 h-10 text-primary mx-auto mb-3" />
        <h3 className="font-semibold text-foreground mb-2">Earn With Referrals</h3>
        <p className="text-sm text-muted-foreground">
          Connect your wallet to get your unique referral link and start earning!
        </p>
      </div>
    );
  }

  return (
    <div className="p-6 rounded-xl bg-card border border-border">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
          <Gift className="w-5 h-5 text-primary" />
        </div>
        <div>
          <h3 className="font-semibold text-foreground">Your Referral Link</h3>
          <p className="text-sm text-muted-foreground">Earn 0.01 SOL per referral who trades</p>
        </div>
      </div>

      <div className="flex gap-2 mb-6">
        <Input 
          value={referralUrl} 
          readOnly 
          className="font-mono text-sm"
        />
        <Button onClick={copyLink} variant="outline">
          {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
        </Button>
      </div>

      <div className="grid grid-cols-3 gap-4">
        <div className="text-center p-3 rounded-lg bg-secondary/50">
          <Users className="w-5 h-5 text-primary mx-auto mb-1" />
          <p className="text-lg font-bold text-foreground">{stats?.total || 0}</p>
          <p className="text-xs text-muted-foreground">Total Referrals</p>
        </div>
        <div className="text-center p-3 rounded-lg bg-secondary/50">
          <Check className="w-5 h-5 text-success mx-auto mb-1" />
          <p className="text-lg font-bold text-foreground">{stats?.completed || 0}</p>
          <p className="text-xs text-muted-foreground">Completed</p>
        </div>
        <div className="text-center p-3 rounded-lg bg-secondary/50">
          <TrendingUp className="w-5 h-5 text-warning mx-auto mb-1" />
          <p className="text-lg font-bold text-foreground">{(stats?.earnings || 0).toFixed(2)}</p>
          <p className="text-xs text-muted-foreground">SOL Earned</p>
        </div>
      </div>
    </div>
  );
}
